import React, { FC, useCallback, useState } from 'react'
import prettyMilliseconds from 'pretty-ms'
import { isMobile } from 'react-device-detect'
import { useCast } from 'react-chromecast'
import platform from 'platform'

import {
  PlayerControlsBox,
  PlayerProgressbar,
  PlayerRow,
  ControlButton,
  ControlsButtonWrapper,
} from '../styles/VideoScreenStyles'

import PlayIcon from '../../../assets/REDESIGN_Assets/Player/play.png'
import RewindIcon from '../../../assets/REDESIGN_Assets/Player/rewind.png'
import ForwardIcon from '../../../assets/REDESIGN_Assets/Player/forward.png'
import PiPIcon from '../../../assets/REDESIGN_Assets/Player/pip.webp'
import FullscreenIcon from '../../../assets/REDESIGN_Assets/Player/fullscreen.png'
import PauseIcon from '../../../assets/REDESIGN_Assets/Player/pause.png'
import CastIcon from '../../../assets/REDESIGN_Assets/Player/cast-icon.svg'

import { AiFillHeart, AiOutlineHeart } from 'react-icons/ai'

import GenericIcon from '../../../GenericComponents/GenericIcon'
import GenericTextSpan from '../../../GenericComponents/GenericTextSpan'
import GenericRow from '../../../GenericComponents/GenericRow'

interface PlayerControlsProps {
  playedPercentage: number
  handleForwardRewindButton: Function
  handleTogglePlay: Function
  isVideoPlaying: boolean
  handleClickFullscreen: Function
  controlsRef: Function
  isFullscreen: boolean
  playedSeconds: number
  handlePictureInPictureClick: Function
  videoName: string
  videoDuration: number
  albumName: string
  shouldHideControls: boolean
  isHorizontalMobile: boolean
  toggleFavorite: Function
  isFav: boolean
}

const PlayerControls: FC<PlayerControlsProps> = ({
  controlsRef,
  playedPercentage,
  handleForwardRewindButton,
  handleTogglePlay,
  isVideoPlaying,
  handleClickFullscreen,
  isFullscreen,
  playedSeconds,
  handlePictureInPictureClick,
  videoName,
  videoDuration,
  albumName,
  shouldHideControls,
  isHorizontalMobile,
  toggleFavorite,
  isFav,
}) => {
  const [toggleFav, setToggleFav] = useState(isFav)
  const CastButton = () => {
    const cast = useCast({
      initialize_media_player: '96BA8A62',
      auto_initialize: true,
    })
    const handleClick = useCallback(async () => {
      if (cast.castReceiver) {
        await cast.handleConnection()
      }
    }, [cast.castReceiver, cast.handleConnection])

    return (
      <ControlButton margin={'0 1vw 0 1vw'} onClick={handleClick}>
        <GenericIcon
          width={isMobile ? '3.5vh' : '1.4vw'}
          height={isMobile ? '3.5vh' : '1.4vw'}
          src={CastIcon}
        />
      </ControlButton>
    )
  }

  const togglFav = () => {
    setToggleFav(!toggleFav)
    toggleFavorite()
  }

  return (
    <PlayerControlsBox
      isFullscreen={isFullscreen}
      className={
        isMobile && platform.os.family === 'iOS'
          ? 'is-safari player-controls'
          : 'player-controls'
      }
      shouldHideControls={shouldHideControls}
      ref={controlsRef}
      isMobile={isMobile}
      isHorizontalMobile={isHorizontalMobile}
    >
      {isMobile && platform.os.family !== 'iOS' && (
        <PlayerRow>
          <ControlsButtonWrapper justify={'center'}>
            <ControlButton onClick={() => handleClickFullscreen()}>
              <GenericIcon
                className="player-icon"
                width={isMobile ? '3.5vh' : '1.4vw'}
                height={isMobile ? '3.5vh' : '1.4vw'}
                src={FullscreenIcon}
              />
            </ControlButton>
          </ControlsButtonWrapper>
        </PlayerRow>
      )}

      <PlayerRow>
        <PlayerProgressbar value={playedPercentage}></PlayerProgressbar>
      </PlayerRow>
      <PlayerRow margin={'5px'} justify={'space-between'}>
        <GenericTextSpan display={'block'} fSize={'0.8rem'}>
          {playedSeconds < 1
            ? '0:00'
            : prettyMilliseconds(Math.floor(playedSeconds) * 1000, {
                colonNotation: true,
                compact: true,
                millisecondsDecimalDigits: 1,
                keepDecimalsOnWholeSeconds: false,
              })}
        </GenericTextSpan>

        <GenericTextSpan
          display={'block'}
          fSize={'0.8rem'}
          margin={'0 10px 0 0'}
        >
          {videoDuration &&
            prettyMilliseconds(videoDuration * 1000, {
              colonNotation: true,
              compact: true,
              millisecondsDecimalDigits: 1,
              keepDecimalsOnWholeSeconds: false,
            })}
        </GenericTextSpan>
      </PlayerRow>

      <PlayerRow margin={'5px 0'}>
        {!isMobile && (
          <ControlsButtonWrapper justify={'flex-start'}>
            <GenericTextSpan>{videoName}</GenericTextSpan>
          </ControlsButtonWrapper>
        )}

        <ControlsButtonWrapper justify={'space-around'}>
          <ControlButton onClick={() => handleForwardRewindButton('back')}>
            <GenericIcon
              className="player-icon"
              width={isMobile ? '3.5vh' : '1.4vw'}
              height={isMobile ? '3.5vh' : '1.4vw'}
              src={RewindIcon}
            />
          </ControlButton>

          <ControlButton onClick={() => handleTogglePlay()}>
            <GenericIcon
              className="player-icon"
              width={isMobile ? '2.3vh' : '1vw'}
              height={isMobile ? '2.3vh' : '1vw'}
              src={isVideoPlaying ? PauseIcon : PlayIcon}
            />
          </ControlButton>

          <ControlButton
            className={
              albumName.includes('MIAZGA') || albumName.includes('SZPAGAT')
                ? 'inactive'
                : ''
            }
            onClick={() => handleForwardRewindButton('forward')}
          >
            <GenericIcon
              width={isMobile ? '3.5vh' : '1.4vw'}
              height={isMobile ? '3.5vh' : '1.4vw'}
              src={ForwardIcon}
              className="player-icon"
            />
          </ControlButton>
        </ControlsButtonWrapper>
        {!isMobile && (
          <ControlsButtonWrapper justify={'flex-end'}>
            <ControlButton
              onClick={() => handlePictureInPictureClick()}
              // margin={'0 1vw 0 0'}
            >
              <GenericIcon
                width={isMobile ? '3.5vh' : '1.4vw'}
                height={isMobile ? '3.5vh' : '1.4vw'}
                src={PiPIcon}
              />
            </ControlButton>

            <ControlButton onClick={() => handleClickFullscreen()}>
              <GenericIcon
                width={isMobile ? '3.5vh' : '1.4vw'}
                height={isMobile ? '3.5vh' : '1.4vw'}
                src={FullscreenIcon}
              />
            </ControlButton>

            <ControlButton onClick={() => togglFav()}>
              <GenericRow
                width={isMobile ? '3.5vh' : '1.4vw'}
                height={isMobile ? '3.5vh' : '1.4vw'}
                // margin={'0 0 0 1vw'}
              >
                {toggleFav ? (
                  <AiFillHeart className="heart-fill" />
                ) : (
                  <AiOutlineHeart className="heart-fill" />
                )}
              </GenericRow>
            </ControlButton>

            {/* {platform.name == 'Chrome' && !isMobile && CastButton()} */}
          </ControlsButtonWrapper>
        )}
      </PlayerRow>
    </PlayerControlsBox>
  )
}

export default PlayerControls
